import { useEffect } from "react";

import { Sparkles, Wrench, MessageSquare, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useOnboardingTracking } from "@/hooks/useOnboardingTracking";
import { ROUTES } from "@/lib/routes";

interface PathSelectorProps {
  onSelectPath: (path: "prebuilt" | "custom" | "braindump") => void;
}

const PathSelector = ({ onSelectPath }: PathSelectorProps) => {
  const navigate = useNavigate();
  const { trackEvent } = useOnboardingTracking();

  // Track when user lands on path selection
  useEffect(() => {
    trackEvent("path_selection_started");
  }, [trackEvent]);

  const handlePathSelect = (path: "prebuilt" | "custom" | "braindump") => {
    trackEvent("path_selected", { path });
    onSelectPath(path);
  };

  const handleNotReady = () => {
    sessionStorage.setItem('skipDashboardRedirect', 'true');
    navigate(ROUTES.QUICK_START);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-semibold">Create Your Advisory Board</h1>
        <p className="text-muted-foreground">
          Choose the path that fits your current energy and clarity level
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card 
          className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
          onClick={() => handlePathSelect("prebuilt")}
        >
          <CardHeader>
            <div className="mb-2">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>Start with a Pre-built Board</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-sm">
              The fastest way to get started. Choose a battle-tested advisory board for a common business type. 
              Perfect for when you're low on energy or want to try a proven setup.
            </CardDescription>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
          onClick={() => handlePathSelect("custom")}
        >
          <CardHeader>
            <div className="mb-2">
              <Wrench className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>Build a Custom Board (Guided)</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-sm">
              For when you have a specific problem to solve. We'll ask a few questions and then help you 
              build a board tailored to your unique challenge.
            </CardDescription>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
          onClick={() => handlePathSelect("braindump")}
        >
          <CardHeader>
            <div className="mb-2">
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>I'm Not Sure What I Need</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-sm">
              Feeling overwhelmed or unclear? No problem. Start with a simple brain dump, and we'll help 
              you discover the right experts to guide you.
            </CardDescription>
          </CardContent>
        </Card>
      </div>

      <div className="text-center mt-8 pt-6 border-t border-border">
        <button
          onClick={handleNotReady}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
        >
          <BookOpen className="h-4 w-4" />
          Not ready yet? Read the quick start guide first
        </button>
      </div>
    </div>
  );
};

export default PathSelector;
